import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useApi } from "../api/ApiContext";

const AuthContext = createContext();

// Basic sanity check on a token string before using it
function isValidTokenShape(t) {
  return typeof t === "string" && t.length >= 10 && t.length <= 512 && /^[\w.\-+/=]+$/.test(t);
}

// Strip sensitive fields from a user payload before storing in state
function sanitizeUser(data) {
  if (!data || typeof data !== "object") return null;
  const { token, password, ...safe } = data; // eslint-disable-line no-unused-vars
  return safe;
}

export function AuthProvider({ children }) {
  const api = useApi();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  // On mount: if a token exists in localStorage, verify it with the server
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !isValidTokenShape(token)) {
      // No token or malformed value → clear and continue unauthenticated
      localStorage.removeItem("token");
      setChecking(false);
      return;
    }
    api
      .get("/me.php")
      .then(res => {
        if (res.data?.id) setUser(sanitizeUser(res.data));
        else {
          setUser(null);
          localStorage.removeItem("token");
        }
      })
      .catch(() => {
        setUser(null);
        localStorage.removeItem("token");
      })
      .finally(() => setChecking(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    // Basic input guard (real validation is on the server)
    if (typeof email !== "string" || typeof password !== "string") return false;
    if (!email.trim() || !password) return false;
    try {
      const res = await api.post("/login.php", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (res.data?.success && isValidTokenShape(res.data.token)) {
        localStorage.setItem("token", res.data.token);
        // Store user data without the token itself
        setUser(sanitizeUser(res.data));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [api]);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, checking }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
